import React, { useState, useEffect } from 'react';
import Typography from '@material-ui/core/Typography';

import { IDevProfile } from '../../../interfaces/dev-profile';
import { getDevProfile, postDevProfile } from '../../../services/HatApiService';
import { useHatUrl, useUser } from '../../../helpers/customHooks';
import { getUnixTimestamp } from '../../../helpers/date-helper';
import Paper from '../../../styles/templates/Paper';
import TextField from '../../../styles/templates/DSTextField';
import { useProfileStyles } from './styles';
import { ContentRow } from '../../../styles/templates/PageTemplates';

const checkForChanges = (check: IDevProfile, existing?: IDevProfile): boolean => {
  if (!existing) return false;

  return (
    existing.personal.firstName !== check.personal.firstName ||
    existing.personal.lastName !== check.personal.lastName ||
    existing.online?.website !== check.online?.website
  );
};

const ProfileTab: React.FC = () => {
  const classes = useProfileStyles();
  const user = useUser();
  const hatUrl = useHatUrl();
  const [devProfile, setDevProfile] = useState<IDevProfile>();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [website, setWebsite] = useState('');

  useEffect(() => {
    const timer = setTimeout(async () => {
      const newProfile: IDevProfile = {
        personal: {
          firstName,
          lastName,
        },
        online: {
          website,
        },
        dateCreated: getUnixTimestamp(),
      };

      if (checkForChanges(newProfile, devProfile)) await postDevProfile(`${hatUrl}`, newProfile);
    }, 1000);

    return () => clearTimeout(timer);
  }, [firstName, lastName, website, hatUrl, devProfile]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!hatUrl) return;
      const response = await getDevProfile(hatUrl);

      if (!response.parsedBody || response.parsedBody.length === 0) return;
      const devProfile = response.parsedBody[0].data;

      setDevProfile(devProfile);
      setFirstName(devProfile.personal.firstName);
      setLastName(devProfile.personal.lastName);
      setWebsite(devProfile.online?.website || '');
    };

    fetchProfile();
  }, [hatUrl]);

  return (
    <div>
      <Paper>
        <div className={classes.container}>
          <div>
            <Typography variant="subtitle1">User Profile</Typography>
            <Typography variant="body2" className={classes.subtitle}>
              View your Personal URL which will display your public information
            </Typography>
          </div>
          <Typography>
            <b>{user?.email}</b>
          </Typography>
        </div>
      </Paper>

      <Paper>
        <div className={classes.container}>
          <div>
            <Typography variant="subtitle1">Personal Details</Typography>
            <Typography variant="body2" className={classes.subtitle}>
              Add or Edit personal details and mark each as public or private
            </Typography>
          </div>

          <ContentRow>
            <TextField
              id="firstname"
              name="firstname"
              label="First name"
              variant="outlined"
              className="formInput"
              autoComplete="off"
              value={firstName || ''}
              onChange={(e) => setFirstName(e.target.value)}
              focused={false}
            />

            <TextField
              id="lastname"
              name="lastname"
              label="Last name"
              variant="outlined"
              className="formInput"
              autoComplete="off"
              value={lastName || ''}
              onChange={(e) => setLastName(e.target.value)}
              focused={false}
            />
          </ContentRow>

          <ContentRow>
            <TextField
              id="website"
              name="website"
              label="Personal Website"
              variant="outlined"
              className="formInput"
              autoComplete="off"
              value={website || ''}
              onChange={(e) => setWebsite(e.target.value || '')}
              focused={false}
            />
          </ContentRow>
        </div>
      </Paper>
    </div>
  );
};

export default ProfileTab;
